/* eslint-disable no-alert */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as Styles from '@components/modals/styles';
import { ThumbnailBoard } from '@components/thumbnail-board';
import { IBoard } from '@interface/board';
import { ILooseObject } from '@interface/loose-object';
import { boardModify } from '@lib/versum';
import { actions } from '@state/modals';
import { testNodeHealth } from '@utils/node';
import { getSmallestImageBoardPreview } from '@utils/token';
import { AlertDialog, Box, Button, Checkbox, Field, Input, Textarea } from '@versumstudios/ui';

export interface IBoardEdit {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  callback?: (result?: any) => void;
  board?: IBoard;
  tempUri?: string;
}

export const BoardEdit: FC<IBoardEdit> = ({ open, onOpenChange, callback, board, tempUri }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState<ILooseObject>({
    title: { error: false, data: board?.title },
    description: { error: false, data: board?.description },
    // tempUri is a temp string that comes from the edit cover window
    cover: { error: false, data: tempUri || board?.cover_uri },
    privacy: { error: false, data: board?.private },
  });

  const handleChange = (key: string, value: any) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: false, data: value },
    }));
  };

  const setError = (key: string, message: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: { error: message, data: prevValues[key].data },
    }));
  };

  const validate = () => {
    let valid = true;

    if (!values.title.data) {
      setError('title', '"Title" is missing');
      valid = false;
    }

    if (!values.description.data) {
      setError('description', '"Description" is missing');
      valid = false;
    }

    if (board?.tokens.length === 4 && !values.cover.data) {
      setError('cover', '"Cover" is missing');
      valid = false;
    }

    return valid;
  };

  const handleConfirm = async () => {
    if (validate()) {
      if (board) {
        await testNodeHealth();
        boardModify(board.id, values.title.data, values.description.data, values.cover.data, values.privacy.data).then(
          (result) => {
            if (callback) callback(result);
          }
        );
      }
      if (onOpenChange) {
        onOpenChange(false);
      }
    }
  };

  const handleCancel = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const promptEditCoverModal = () => {
    if (board) {
      const cb = (_success: boolean, result?: any) => {
        // user cancelled
        dispatch({
          type: actions.MODALS_SET_MODAL,
          payload: {
            type: 'board-edit',
            board,
            tempUri: result?.uri,
          },
        });
      };

      dispatch({
        type: actions.MODALS_SET_MODAL,
        payload: {
          type: 'board-cover-edit',
          board,
          callback: cb,
        },
      });
    }
  };

  const promptDeleteModal = () => {
    if (board) {
      const cb = (isConfirmCallback: boolean, result: any) => {
        if (isConfirmCallback) {
          // user confirmed
          if (callback) callback(result);
          if (onOpenChange) {
            onOpenChange(false);
          }
        } else {
          // user cancelled
          dispatch({
            type: actions.MODALS_SET_MODAL,
            payload: {
              type: 'board-edit',
              board,
            },
          });
        }
      };

      dispatch({
        type: actions.MODALS_SET_MODAL,
        payload: {
          type: 'board-delete',
          board,
          callback: cb,
        },
      });
    }
  };

  const images = board
    ? board.tokens.map(({ token }: any) => getSmallestImageBoardPreview(token)).map((e: any) => e.src)
    : [];

  return (
    <AlertDialog title="Edit Board" open={open} onConfirm={handleConfirm} onCancel={handleCancel}>
      <Styles.Content>
        <Field uid="title" label="Title" error={values.title.error}>
          <Input
            type="text"
            placeholder="e.g.: Lorem ipsum"
            defaultValue={values.title.data}
            onChange={(value: string) => handleChange('title', value)}
          />
        </Field>

        <Field uid="description" label="Description" error={values.description.error}>
          <Textarea
            placeholder="e.g.: dolor sit amet"
            defaultValue={values.description.data}
            onChange={(value: string) => handleChange('description', value)}
          />
        </Field>

        <Field uid="cover" label="Cover" error={values.cover.error}>
          <Button variant="basic" onClick={promptEditCoverModal}>
            <Box css={{ display: 'block', width: 200, height: 200 }}>
              <ThumbnailBoard cover={values.cover.data} images={images} />
            </Box>
          </Button>
        </Field>

        <Field uid="settings" label="Settings" error={values.privacy.error}>
          <Checkbox
            value={values.privacy.data}
            label="Keep this board private"
            onChange={(value: boolean) => handleChange('privacy', value)}
            reversed
          />
        </Field>

        <Field uid="delete" label="Danger Zone">
          <Button variant="secondary" onClick={promptDeleteModal} full>
            Delete board
          </Button>
        </Field>
      </Styles.Content>
    </AlertDialog>
  );
};
